import React from 'react';
import {
  Alert,
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';

/**
 * MAP 3P Bedrock policy (P530558159): the calculator Lambda's
 * fundingEligibility.bedrock3pPolicy block, and the agent's effectiveAmounts.
 *
 * Presentation only. Every share, cap term, amount and total below is a value
 * the Lambda or the agent computed; this module labels and formats them. It
 * never derives a value, compares a date, or decides which amounts apply. The
 * FR page passes the block the agent persisted; the standalone Analyzer passes
 * the live block from its own response (`analyzer`), which adds the base and
 * funded amounts side by side and the configured dates.
 */

// The two denominators, in the order the agent's messages print them.
const BASES = [
  { key: 'qualification', label: 'Qualified ARR', description: 'after exclusions and SP/RI' },
  { key: 'gross', label: 'Gross deal ARR', description: 'before exclusions and SP/RI' },
];

// Only these classes are Bedrock lines; the block's other lines (native,
// ARR-only, excluded) are not listed in the panel.
const POLICY_CLASS_LABELS = {
  bedrock_3p: '3P model provider',
  bedrock_1p: '1P (Amazon) model',
  bedrock_unknown: 'Provider unknown (counted as 3P)',
};

const MAP_FUNDED_BASIS_LABELS = {
  native_plus_funded_3p: 'AWS-native plus funded 3P',
  qualification_minus_unfunded_3p: 'qualified ARR minus unfunded 3P',
};

// Step 2 amounts by their path in amounts.base / amounts.funded: the keys the
// agent's effectiveAmounts rows carry.
const AMOUNT_LABELS = {
  'partnerCash.assess': 'Assess',
  'partnerCash.mobilize': 'Mobilize',
  'partnerCash.greenfield': 'Greenfield SPI',
  'partnerCash.modernization': 'Modernization Pathways SPI',
  poc: 'PoC',
  'awsCredits.migrateAndModernize': 'Migrate & Modernize credits',
  'awsCredits.sapAndOracle': 'SAP & Oracle credits',
  'awsCredits.dbAndAnalytics': 'DB & Analytics credits',
};

// The block's totals count every incentive; the Analyzer's Total MAP Funding
// container below applies the reviewer toggles.
const TOTAL_LABELS = {
  totalPartnerCash: 'Total Partner Cash (all incentives, before toggles)',
  totalAWSCredits: 'Total AWS Credits (all incentives, before toggles)',
  totalMAPFunding: 'Total MAP Funding (all incentives, before toggles)',
};

const SOURCE_LABELS = {
  base: 'Base',
  funded: 'Funded',
  undetermined_conservative: 'Undetermined (conservative)',
};

const APPLICABILITY_LABELS = {
  APPLIES: 'Applies',
  DOES_NOT_APPLY: 'Does not apply',
  UNDETERMINED: 'Undetermined',
  NOT_REQUESTED: 'Not requested',
};

const COMPONENT_LABELS = { cash: 'Cash', credit: 'Credit' };

// Persisted numbers can arrive as numbers or numeric strings. Anything else
// renders as a dash, never as a guessed zero.
const toNumber = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));
const money = (formatCurrency, value) => {
  const n = toNumber(value);
  return Number.isFinite(n) ? formatCurrency(n) : '—';
};
const percent = (value) => {
  const n = toNumber(value);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : '—';
};
const yesNo = (value) => (value === true ? 'Yes' : value === false ? 'No' : '—');
const amountAt = (amounts, key) =>
  key.split('.').reduce((node, part) => (node ? node[part] : undefined), amounts)?.amount;

const lineKey = (line) => `${line.parentServiceCode || ''}|${line.serviceCode}|${line.policyClass}`;

const KeyValue = ({ label, children }) => (
  <div>
    <Box variant="awsui-key-label">{label}</Box>
    <Box>{children}</Box>
  </div>
);

const Bedrock3pPolicyPanel = ({ block, formatCurrency, analyzer = false }) => {
  if (!block || typeof block !== 'object') return null;

  const header = (
    <Header
      variant="h3"
      description={`Rule ${block.ruleId || 'MAP-BEDROCK-3P'}, config version ${block.configVersion ?? '—'}`}
    >
      3P Bedrock Policy
    </Header>
  );

  if (block.status !== 'ok') {
    const notices = {
      config_error: 'The calculator could not load the 3P Bedrock policy configuration (config_error), so no share or cap was computed.',
      data_error: 'The calculator deal ARR is zero, negative or not a number (data_error), so no share or cap was computed.',
    };
    return (
      <Container header={header}>
        <Alert type="warning" header="3P Bedrock share and cap not evaluated">
          {notices[block.status] || `Unrecognized policy status "${block.status}"; the share and cap are not shown.`}
        </Alert>
      </Container>
    );
  }

  const lines = Array.isArray(block.lines) ? block.lines : [];
  if (lines.length === 0) return null;

  const fc = (value) => money(formatCurrency, value);
  const config = block.config || {};
  const thresholds = config.thresholds || {};
  const enums = config.enums || {};
  const dates = config.dates || {};
  const shares = block.shares || {};
  const cap = block.cap || {};
  const bedrockLines = lines.filter((line) => POLICY_CLASS_LABELS[line.policyClass]);
  const amounts = block.amounts || {};

  return (
    <Container header={header}>
      <SpaceBetween size="l">
        <Table
          variant="embedded"
          header={<Header variant="h3">3P share of deal ARR</Header>}
          trackBy="key"
          columnDefinitions={[
            {
              id: 'denominator',
              header: 'Denominator',
              cell: (item) => (
                <div>
                  <Box variant="strong">{item.label}</Box>
                  <Box variant="small">{item.description}</Box>
                </div>
              ),
            },
            { id: 'dealArr', header: 'Deal ARR', cell: (item) => fc(item.share.dealArr) },
            { id: 'known3pArr', header: 'Known 3P ARR', cell: (item) => fc(item.share.known3pArr) },
            { id: 'knownPct', header: 'Known share', cell: (item) => percent(item.share.knownPct) },
            { id: 'unknownArr', header: 'Provider-unknown Bedrock ARR', cell: (item) => fc(item.share.unknownArr) },
            { id: 'maxPct', header: 'Max share (conservative estimate)', cell: (item) => percent(item.share.maxPct) },
            {
              id: 'exceeds',
              header: `Above ${thresholds.gateThresholdPct ?? '—'}%`,
              cell: (item) => `Known: ${yesNo(item.share.knownExceeds)}, max: ${yesNo(item.share.maxExceeds)}`,
            },
          ]}
          items={BASES.map((basis) => ({ ...basis, share: shares[basis.key] || {} }))}
        />

        <SpaceBetween size="s">
          <Box variant="h4">Cap terms (qualified ARR basis)</Box>
          <Box variant="small">
            Bedrock lines without a known model provider count as 3P, so the subject and every term
            derived from it are a conservative estimate.
          </Box>
          <ColumnLayout columns={4} variant="text-grid">
            <KeyValue label="Base: AWS-native ARR">{fc(cap.baseArr)}</KeyValue>
            <KeyValue label="Subject: 3P and provider-unknown ARR (conservative estimate)">{fc(cap.subjectArr)}</KeyValue>
            <KeyValue label={`Limit: ${thresholds.capPct ?? '—'}% of base`}>{fc(cap.limitArr)}</KeyValue>
            <KeyValue label="Funded 3P ARR">{fc(cap.fundedArr)}</KeyValue>
            <KeyValue label="Unfunded 3P ARR">{fc(cap.unfundedArr)}</KeyValue>
            <KeyValue label="AWS-native ARR needed">{fc(cap.nativeArrNeeded)}</KeyValue>
            <KeyValue label="Cap binds">{yesNo(cap.binding)}</KeyValue>
            <KeyValue label={`MAP-Funded ARR (${MAP_FUNDED_BASIS_LABELS[enums.mapFundedBasis] || enums.mapFundedBasis || '—'})`}>
              {fc(cap.selectedMapFundedArr)}
            </KeyValue>
          </ColumnLayout>
        </SpaceBetween>

        <Table
          variant="embedded"
          header={<Header variant="h3">Bedrock lines</Header>}
          trackBy={lineKey}
          columnDefinitions={[
            { id: 'parentServiceCode', header: 'Parent service code', cell: (line) => line.parentServiceCode || '—' },
            { id: 'serviceCode', header: 'Service code', cell: (line) => line.serviceCode },
            { id: 'policyClass', header: 'Policy class', cell: (line) => POLICY_CLASS_LABELS[line.policyClass] },
            { id: 'grossArr', header: 'Gross ARR', cell: (line) => fc(line.grossArr) },
            { id: 'qualificationArr', header: 'Qualified ARR', cell: (line) => fc(line.qualificationArr) },
          ]}
          items={bedrockLines}
          empty={<Box textAlign="center">No Bedrock lines in this calculator.</Box>}
        />

        {analyzer && (
          <SpaceBetween size="s">
            <Box variant="h4">Base and funded amounts</Box>
            <Box variant="small">
              {`Rule 1: 3P Bedrock above ${thresholds.gateThresholdPct ?? '—'}% of deal ARR is not MAP eligible for requests submitted on or after ${dates.gateAppliesAfter || '—'}. `}
              {`Rule 2: 3P Bedrock above ${thresholds.capPct ?? '—'}% of AWS-native ARR is not counted from ${dates.capEffectiveOn || '—'}.`}
            </Box>
            <Box variant="small">Cash/credit applicability is determined on the fund request.</Box>
            {!amounts.funded && (
              <Box variant="small">No 3P or provider-unknown Bedrock ARR, so there are no funded amounts.</Box>
            )}
            {amounts.funded && enums.mapFundedBasis === 'native_plus_funded_3p' && cap.binding === false && (
              <Box variant="small">
                The cap does not bind. Funded can still differ from base because of the selected MAP-Funded basis:
                AWS-native plus funded 3P leaves out ARR-only services. The difference is the basis, not the cap.
              </Box>
            )}
            <Table
              variant="embedded"
              trackBy="key"
              columnDefinitions={[
                { id: 'label', header: 'Amount', cell: (item) => item.label },
                { id: 'base', header: 'Base', cell: (item) => item.base },
                { id: 'funded', header: 'Funded (3P above the cap removed)', cell: (item) => item.funded },
              ]}
              items={[
                {
                  key: 'programTier',
                  label: 'Program tier',
                  base: amounts.base?.programTier || '—',
                  funded: amounts.funded?.programTier || '—',
                },
                ...Object.entries(AMOUNT_LABELS).map(([key, label]) => ({
                  key,
                  // PoC is its own component, outside the three totals below.
                  label: key === 'poc' ? `${label} (not in totals)` : label,
                  base: fc(amountAt(amounts.base, key)),
                  funded: fc(amountAt(amounts.funded, key)),
                })),
                ...Object.entries(TOTAL_LABELS).map(([key, label]) => ({
                  key,
                  label,
                  base: fc(amounts.base?.totals?.[key]),
                  funded: fc(amounts.funded?.totals?.[key]),
                })),
              ]}
            />
          </SpaceBetween>
        )}

        <Box variant="small" color="text-body-secondary">
          {block.coverage === 'calculator_only'
            ? 'Coverage: pricing calculator lines only.'
            : `Coverage: ${block.coverage || '—'}.`}
          {` Gate mode: ${enums.gateMode || '—'}. Cap mode: ${enums.capMode || '—'}. Config version ${block.configVersion ?? '—'}.`}
        </Box>
      </SpaceBetween>
    </Container>
  );
};

/**
 * Step 2 rows and totals from the agent's effectiveAmounts (capMode enforce).
 * Each row is the base or funded amount the agent selected for it, with its
 * applicability, mode and reason; the totals are the agent's sums of those
 * rows, with PoC in neither, as in the legacy totals.
 */
export const Bedrock3pEffectiveAmounts = ({ effectiveAmounts, formatCurrency }) => {
  const fc = (value) => money(formatCurrency, value);
  const rows = effectiveAmounts.rows;
  const totals = effectiveAmounts.totals || {};

  return (
    <SpaceBetween size="m">
      <Box variant="small">
        {`Amounts as the MAP agent selected them under the 3P Bedrock cap${rows[0]?.mode ? ` (cap mode ${rows[0].mode})` : ''}. `}
        Each row is the base amount or the funded amount, which removes the 3P ARR above the cap.
      </Box>
      <Table
        variant="embedded"
        trackBy="key"
        columnDefinitions={[
          { id: 'type', header: 'Funding Type', cell: (item) => AMOUNT_LABELS[item.key] || item.key },
          { id: 'component', header: 'Cap component', cell: (item) => COMPONENT_LABELS[item.component] || item.component || '-' },
          {
            id: 'applicability',
            header: 'Applicability',
            cell: (item) => APPLICABILITY_LABELS[item.applicability] || item.applicability || '-',
          },
          { id: 'reason', header: 'Reason', cell: (item) => item.reason || '-' },
          { id: 'source', header: 'Source', cell: (item) => SOURCE_LABELS[item.source] || item.source || '-' },
          { id: 'amount', header: 'Amount', cell: (item) => <Box fontWeight="bold">{fc(item.amount)}</Box> },
          { id: 'notes', header: 'Notes', cell: (item) => (item.key === 'poc' ? 'Not included in totals' : '-') },
        ]}
        items={rows}
      />
      <Box textAlign="right">
        <Box variant="strong">Total Partner Cash (agent-computed maximum): {fc(totals.cash)}</Box>
      </Box>
      <Box textAlign="right">
        <Box variant="strong">Total AWS Credits (agent-computed maximum): {fc(totals.credit)}</Box>
      </Box>
      <Box variant="small">
        {"These totals are the MAP agent's computed maximum: they include Greenfield SPI, SAP & Oracle and DB & "
          + 'Analytics as computed and exclude PoC. The reviewer toggles under Total MAP Funding set the '
          + 'reviewer-adjusted total and do not change them.'}
      </Box>
    </SpaceBetween>
  );
};

export default Bedrock3pPolicyPanel;
